<template>
	<div>
		<!-- Page Header -->
		<JuniperPageHeader back="/juniper" title="Juniper Clients" subtitle="Manage all your Juniper CMS clients">
			<router-link to="/juniper/pdf-auditor" class="j-icon-btn">
				<font-awesome-icon :icon="['fas', 'file-pdf']" class="j-icon-btn-icon" />
				<span class="j-icon-btn-tooltip">PDF Auditor</span>
			</router-link>
			<router-link to="/juniper/clients/new" class="j-icon-btn">
				<font-awesome-icon :icon="['fas', 'plus']" class="j-icon-btn-icon" />
				<span class="j-icon-btn-tooltip">Create Client</span>
			</router-link>
		</JuniperPageHeader>

		<!-- Juniper Clients Section -->
		<div class="clients-section mb-4">

			<!-- Filter Tabs Container -->
			<div class="filter-tabs-container">
			<div class="filter-tabs">
				<!-- Environment Filters -->
				<button class="filter-tab environment-tab" :class="{ active: environmentFilter === 'all' }" @click="setEnvironmentFilter('all')">
					<span class="filter-tab-text">All</span>
					<span class="filter-tab-badge">{{ stats ? stats.total_count : 0 }}</span>
				</button>
				<button class="filter-tab environment-tab" :class="{ active: environmentFilter === 'live' }" @click="setEnvironmentFilter('live')">
					<i class="fad fa-globe me-2"></i>
					<span class="filter-tab-text">Public</span>
					<span class="filter-tab-badge">{{ stats ? stats.live_count : 0 }}</span>
				</button>
				<button class="filter-tab environment-tab" :class="{ active: environmentFilter === 'staging' }" @click="setEnvironmentFilter('staging')">
					<i class="fad fa-flask me-2"></i>
					<span class="filter-tab-text">Staging</span>
					<span class="filter-tab-badge">{{ stats ? stats.staging_count : 0 }}</span>
				</button>

				<!-- Divider -->
				<div class="tabs-divider"></div>

				<!-- Advanced Filter -->
				<ModernFilter :filter-items="filterItems" @update:filter="updateFilter" />
			</div>

			<!-- Search -->
			<div class="search-input-wrapper">
				<i class="fad fa-search search-icon"></i>
				<input type="text" class="search-input" placeholder="Search clients (press Enter)" v-model.trim="request.search" @keyup.enter="onSearchSubmit" />
				<button
					v-if="request.search"
					type="button"
					class="search-clear-btn"
					aria-label="Clear search"
					@click="clearSearch"
				>
					<i class="fas fa-times"></i>
				</button>
			</div>
		</div>

		<!-- Clients Table -->
		<div class="clients-table">
				<!-- Table Header -->
				<div class="table-header" :style="tableGridStyle">
					<div class="header-cell client-col">Client</div>
					<div v-if="visibleColumns.type" class="header-cell type-col">Type</div>
					<div v-if="visibleColumns.status" class="header-cell status-col">Status</div>
					<div v-if="visibleColumns.url" class="header-cell url-col">URL</div>
					<div v-if="visibleColumns.static" class="header-cell static-col">Static</div>
					<div v-if="visibleColumns.ada" class="header-cell ada-col">Accessibility</div>
					<div v-if="visibleColumns.launched" class="header-cell launched-col">Launched</div>
					<div v-if="visibleColumns.theme" class="header-cell theme-col">Theme</div>
					<div v-if="visibleColumns.pm" class="header-cell pm-col">PM</div>
					<div class="header-cell actions-col">
						<div class="column-settings" ref="columnSettings">
							<button
								type="button"
								class="column-settings-btn"
								:class="{ 'is-open': columnSettingsOpen }"
								title="Table columns"
								@click="columnSettingsOpen = !columnSettingsOpen"
							>
								<i class="fad fa-columns"></i>
							</button>
							<div v-if="columnSettingsOpen" class="column-settings-dropdown">
								<div class="column-settings-title">Columns</div>
								<label v-for="col in toggleableColumns" :key="col.key" class="column-settings-option">
									<input
										type="checkbox"
										:checked="visibleColumns[col.key]"
										@change="toggleColumn(col.key)"
									/>
									<span>{{ col.label }}</span>
								</label>
							</div>
						</div>
					</div>
				</div>

				<!-- Body: rows stay mounted (blurred) while loading -->
				<div class="table-body-wrap" :class="{ 'is-loading': loading }">
				<div class="table-body" :class="{ 'is-blurred': loading }">

				<!-- Empty State -->
				<div v-if="!loading && clients.length === 0" class="empty-state">
					<div class="empty-state-content">
						<div class="empty-state-icon">
							<i class="fad fa-folder-open"></i>
						</div>
						<h3 class="empty-state-title">No Clients Found</h3>
						<p class="empty-state-description">
							{{ hasActiveFilters() ? 'Try adjusting your filters to see more clients' : 'Get started by creating your first Juniper client' }}
						</p>
						<div class="empty-state-actions" v-if="!hasActiveFilters()">
							<router-link to="/juniper/clients/new" class="empty-state-btn">
								<i class="fad fa-plus me-2"></i>
								Create Your First Client
							</router-link>
						</div>
					</div>
				</div>

				<!-- Table Rows -->
				<div class="table-row" v-for="client in clients" :key="client.id" :style="tableGridStyle">
					<div class="table-cell client-cell">
						<div class="client-info">
							<div class="client-avatar">
								<img v-if="client.logo" :src="client.logo" :alt="client.name" />
								<div v-else class="client-avatar-placeholder">
									<i class="fad fa-building"></i>
								</div>
							</div>
							<div class="client-details">
								<div class="client-name">
									<router-link :to="'/juniper/clients/edit/' + client.id">{{ client.name }}</router-link>
									<span v-if="isUnifiedDashboard(client)" class="dashboard-badge">Unified</span>
								</div>
							</div>
						</div>
					</div>
					<div v-if="visibleColumns.type" class="table-cell type-cell">
						<span class="type-badge" :class="client.type">
							{{ formatType(client.type) }}
						</span>
					</div>
					<div v-if="visibleColumns.status" class="table-cell status-cell">
						<span class="status-badge" :class="getStatusClass(client)">
							<i :class="getStatusIcon(client)"></i>
							{{ formatStatus(client) }}
						</span>
					</div>
					<div v-if="visibleColumns.url" class="table-cell url-cell">
						<div class="url-stack">
							<a v-if="shouldUseStagingUrl(client)" :href="client.staging_url" target="_blank" class="url-link staging">
								<i class="fad fa-flask me-1"></i>
								{{ client.staging_url }}
							</a>
							<a v-else :href="client.url" target="_blank" class="url-link live">
								<i class="fad fa-globe me-1"></i>
								{{ client.url }}
							</a>
							<a
								v-if="shouldShowStagingUrlBelow(client)"
								:href="client.staging_url"
								target="_blank"
								class="url-link staging url-link--secondary"
							>
								<i class="fad fa-flask me-1"></i>
								{{ client.staging_url }}
							</a>
						</div>
					</div>
					<div v-if="visibleColumns.static" class="table-cell static-cell">
						<span
							v-if="client.last_static_generated_at"
							class="static-date"
							data-bs-toggle="tooltip"
							:title="formatStaticAtFull(client.last_static_generated_at)"
						>
							<i class="fab fa-html5 static-html5-icon"></i>
							{{ formatStaticAt(client.last_static_generated_at) }}
						</span>
						<span v-else class="cell-empty">—</span>
					</div>
					<div v-if="visibleColumns.ada" class="table-cell ada-cell">
						<span
							v-if="adaState(client.id).loading"
							class="spinner-border spinner-border-sm ada-spinner"
							role="status"
						></span>
						<button
							v-else-if="adaState(client.id).report"
							type="button"
							class="ada-badges"
							@click="openAdaModal(client)"
						>
							<span
								v-if="adaState(client.id).report.poor_pages > 0"
								class="ada-badge ada-badge--error"
								data-bs-toggle="tooltip"
								data-bs-html="true"
								:title="adaErrorTooltip(adaState(client.id).report)"
							>
								{{ adaState(client.id).report.poor_pages }}
							</span>
							<span
								v-else
								class="ada-badge ada-badge--ok"
								data-bs-toggle="tooltip"
								data-bs-html="true"
								:title="adaOkTooltip(adaState(client.id).report)"
							>
								<i class="fas fa-check"></i>
							</span>
							<span
								v-if="adaState(client.id).report.warn_pages > 0"
								class="ada-badge ada-badge--warning"
								data-bs-toggle="tooltip"
								data-bs-html="true"
								:title="adaWarnTooltip(adaState(client.id).report)"
							>
								{{ adaState(client.id).report.warn_pages }}
							</span>
						</button>
						<span
							v-else
							class="cell-empty"
							data-bs-toggle="tooltip"
							title="No finished ADA audits yet"
						>—</span>
					</div>
					<div v-if="visibleColumns.launched" class="table-cell launched-cell">
						<span class="launched-date" :title="formatLaunchedAtFull(client.launched_at)">
							{{ formatLaunchedAt(client.launched_at) }}
						</span>
					</div>
					<div v-if="visibleColumns.theme" class="table-cell theme-cell">
						<span class="theme-badge">
							<i class="fad fa-palette me-1 theme-palette-icon"></i>
							{{ formatTheme(client.theme) }}
						</span>
					</div>
					<div v-if="visibleColumns.pm" class="table-cell pm-cell">
						<span v-if="projectManagerName(client)" class="pm-name" :title="projectManagerName(client)">
							{{ projectManagerName(client) }}
						</span>
						<span v-else class="cell-empty">—</span>
					</div>
					<div class="table-cell actions-cell">
						<div class="actions-row">
						<button
							class="action-icon-btn action-icon-btn--orange static-gen-btn"
							:class="{ 'is-generating': isGeneratingStatic(client.id) }"
							:disabled="isGeneratingStatic(client.id)"
							data-bs-toggle="tooltip"
							:title="isGeneratingStatic(client.id) ? 'Generating full static…' : 'Generate Full Static — rebuilds the static HTML copy of the whole site that is served to visitors'"
							@click="generateStatic(client)"
						>
							<template v-if="isGeneratingStatic(client.id)">
								<span class="spinner-border spinner-border-sm static-gen-spinner" role="status"></span>
								<span class="static-gen-seconds">{{ staticGenerating[client.id].seconds }}s</span>
							</template>
							<i v-else class="fas fa-hammer"></i>
						</button>
						<a
							v-if="client.staging"
							:href="client.staging_url"
							target="_blank"
							class="action-icon-btn action-icon-btn--sky"
							title="Visit Staging"
						>
							<i class="fas fa-globe"></i>
						</a>
						<a
							v-else
							:href="client.url"
							target="_blank"
							class="action-icon-btn action-icon-btn--sky"
							title="Visit Site"
						>
							<i class="fas fa-globe"></i>
						</a>
						<router-link
							:to="'/juniper/clients/edit/' + client.id"
							class="action-icon-btn action-icon-btn--blue"
							title="Edit"
						>
							<i class="fad fa-pen"></i>
						</router-link>
						<a
							:href="getDashboardLoginUrl(client)"
							target="_blank"
							class="action-icon-btn action-icon-btn--teal"
							title="Login to Dashboard"
						>
							<i class="fad fa-fingerprint"></i>
						</a>
						</div>
						<!-- Upgrade copy actions sit on their own row, under the live ones -->
						<div v-if="client.upgrade && client.upgrade.id" class="actions-row actions-row--upgrade">
							<router-link
								:to="'/juniper/clients/edit/' + client.upgrade.id"
								class="action-icon-btn action-icon-btn--purple"
								title="Edit upgrade copy"
							>
								<i class="fad fa-pen"></i>
							</router-link>
							<a
								:href="getUpgradeDashboardLoginUrl(client)"
								target="_blank"
								class="action-icon-btn action-icon-btn--purple"
								title="Login to upgrade dashboard"
							>
								<i class="fad fa-fingerprint"></i>
							</a>
						</div>
					</div>
				</div>
				</div>

				<div v-if="loading" class="table-loading">
					<div class="table-loading__box">
						<span class="spinner-border spinner-border-sm me-2" role="status"></span>
						Loading client data…
					</div>
				</div>
				</div>
			</div>

			<!-- Status Info -->
			<div class="status-info-section">
				<div class="status-info-title">Status Definitions:</div>
				<div class="status-info-items">
					<div class="status-info-item">
						<span class="status-badge live"><i class="fad fa-check-circle"></i> Live</span>
						<span class="status-info-text">Client is launched and active</span>
					</div>
					<div class="status-info-item">
						<span class="status-badge preparing"><i class="fad fa-wrench"></i> Preparing</span>
						<span class="status-info-text">Client is in preparation</span>
					</div>
					<div class="status-info-item">
						<span class="status-badge development"><i class="fad fa-code"></i> Development</span>
						<span class="status-info-text">Client used for testing purposes</span>
					</div>
					<div class="status-info-item">
						<span class="status-badge abandoned"><i class="fad fa-times-circle"></i> Abandoned</span>
						<span class="status-info-text">Project was started but abandoned</span>
					</div>
					<div class="status-info-item">
						<span class="status-badge left"><i class="fad fa-arrow-right"></i> Left</span>
						<span class="status-info-text">Client has left us</span>
					</div>
				</div>
			</div>

			<!-- Pagination -->
			<div v-if="pagination && pagination.last_page > 1" class="cl-pagination">
				<div class="cl-pagination__info">
					Showing <strong>{{ pagination.from }}</strong>–<strong>{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong>
				</div>
				<div class="cl-pagination__controls">
					<button
						class="cl-page-btn"
						:disabled="!pagination.prev_page_url"
						@click="paginate(pagination.prev_page_url)"
					>
						<i class="fas fa-chevron-left"></i>
						<span>Prev</span>
					</button>
					<span class="cl-page-current">
						Page {{ pagination.current_page }} <span class="cl-page-current__sep">/</span> {{ pagination.last_page }}
					</span>
					<button
						class="cl-page-btn"
						:disabled="!pagination.next_page_url"
						@click="paginate(pagination.next_page_url)"
					>
						<span>Next</span>
						<i class="fas fa-chevron-right"></i>
					</button>
				</div>
			</div>
		</div>

		<!-- ADA report details modal — same component the ADA Auditor page uses -->
		<ReportDetailsModal
			v-if="adaModal"
			:report-id="adaModal.reportId"
			:client-id="adaModal.clientId"
			@close="adaModal = null"
		/>

		<!-- Full-static toasts -->
		<div class="toast-container position-fixed bottom-0 end-0 p-3 static-toast-container">
			<div
				v-for="toast in staticToasts"
				:key="toast.id"
				class="toast show"
				role="alert"
				aria-live="assertive"
				aria-atomic="true"
			>
				<div class="toast-header">
					<i class="fas fa-hammer me-2 text-success"></i>
					<strong class="me-auto">Full static generated</strong>
					<button type="button" class="btn-close" aria-label="Close" @click="dismissStaticToast(toast.id)"></button>
				</div>
				<div class="toast-body">
					<strong>{{ toast.name }}</strong> — finished in {{ toast.elapsed }}s
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { Tooltip } from 'bootstrap'
import api from '../../../api'
import juniperApi from '../../../api/juniperApi'
import { fromNowUtc, formatUtc } from '@/utils/time'
import { buildDashboardLoginUrl } from '@/utils/dashboardLoginUrl'
import ModernFilter from '../../../components/Filter.vue'
import ReportDetailsModal from '@/components/juniper/ada-auditor/ReportDetailsModal.vue'

// Table column registry — order defines the grid. Client and Actions are
// always visible; everything with a label is toggleable from the header
// column-settings dropdown. Widths mirror the previous static
// grid-template-columns declaration.
const TABLE_COLUMNS = [
	{ key: 'client', width: '2fr', always: true },
	{ key: 'type', label: 'Type', width: '100px' },
	{ key: 'status', label: 'Status', width: '105px' },
	{ key: 'url', label: 'URL', width: '2fr' },
	{ key: 'static', label: 'Static', width: '105px' },
	{ key: 'ada', label: 'Accessibility', width: '110px' },
	{ key: 'launched', label: 'Launched', width: '105px' },
	{ key: 'theme', label: 'Theme', width: '100px' },
	{ key: 'pm', label: 'PM', width: '120px' },
	{ key: 'actions', width: '150px', always: true },
]

const COLUMNS_STORAGE_KEY = 'juniper-clients-visible-columns'
// Hidden out of the box; a saved preference in localStorage wins.
const DEFAULT_HIDDEN_COLUMNS = ['static', 'launched', 'pm']

function loadVisibleColumns() {
	const visible = {}
	TABLE_COLUMNS.filter(col => !col.always).forEach(col => {
		visible[col.key] = !DEFAULT_HIDDEN_COLUMNS.includes(col.key)
	})
	try {
		const saved = JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY))
		if (saved && typeof saved === 'object') {
			Object.keys(visible).forEach(key => {
				if (typeof saved[key] === 'boolean') visible[key] = saved[key]
			})
		}
	} catch (e) {
		// Corrupted/legacy value — fall back to defaults.
	}
	return visible
}

export default {
	name: 'Clients',
	components: {
		ModernFilter,
		ReportDetailsModal,
	},
	data() {
		return {
			loading: false,
			dashboardUrl: process.env.VUE_APP_DASHBOARD_URL,
			clients: [],
			stats: null,
			// Monotonic counters — every getClients()/getClientStats() call increments
			// its counter; only the response that matches the latest counter is applied.
			// Drops out-of-order responses when the user types fast.
			clientsRequestToken: 0,
			statsRequestToken: 0,
			// Per-client ADA audit summary, keyed by client id:
			// { loading, report } — report is the latest finished audit_report
			// (or null when the client has none). Loaded independently per row
			// so the clients table renders without waiting on mc2 audit data.
			adaData: {},
			// Full-static runs in flight, keyed by client id: { seconds, timer }.
			// The generate endpoint is synchronous (responds when the build
			// finishes), so an entry lives here for the whole request and the
			// interval drives the visible seconds counter on the row button.
			staticGenerating: {},
			// Finished-generation toasts: { id, name, elapsed }, newest last.
			staticToasts: [],
			staticToastSeq: 0,
			// Open ADA report modal: { clientId, reportId } or null.
			adaModal: null,
			// Per-user column visibility (persisted in localStorage).
			visibleColumns: loadVisibleColumns(),
			columnSettingsOpen: false,
			pagination: null,
			environmentFilter: 'all',
			request: {
				page: this.$route.query.page || 1,
				search: '',
				staging: '',
				status: '',
				theme: '',
				dashboard: '',
				project_manager_id: '',
				launched_from: '',
				launched_to: '',
				static_from: '',
				static_to: '',
			},
			filterItems: [
				{
					name: 'status',
					label: 'Status',
					icon: 'fad fa-info-circle',
					component: 'SelectItem',
					value: '',
					options: [
						{ id: 'live', name: 'Live' },
						{ id: 'preparing', name: 'Preparing' },
						{ id: 'development', name: 'Development' },
						{ id: 'abandoned', name: 'Abandoned' },
						{ id: 'left', name: 'Left' },
					],
				},
				{
					name: 'theme',
					label: 'Theme',
					icon: 'fad fa-palette',
					component: 'SelectItem',
					value: '',
					options: [
						{ id: 'default', name: 'Maverick' },
						{ id: '2025', name: '2025' },
						{ id: 'city-of-olean', name: 'City of Olean' },
						{ id: 'dynamic-default-landing', name: 'Dynamic Default Landing' },
						{ id: 'hillsdale', name: 'Hillsdale' },
						{ id: 'lightyear', name: 'Lightyear' },
						{ id: 'mockingbird', name: 'Mockingbird' },
						{ id: 'town-of-amenia', name: 'Town of Amenia' },
						{ id: 'town-of-dalton', name: 'Town of Dalton' },
						{ id: 'town-of-medway', name: 'Town of Medway' },
						{ id: 'township-of-millstone', name: 'Township of Millstone' },
						{ id: 'townweb', name: 'TownWeb' },
						{ id: 'village-of-sister-bay', name: 'Village of Sister Bay' },
						{ id: 'willowbay', name: 'Willowbay' },
					],
				},
				{
					name: 'launched_at',
					label: 'Launched Date',
					icon: 'fad fa-calendar-alt',
					component: 'DateRangeItem',
					value: '',
				},
				{
					name: 'static_generated_at',
					label: 'Static Generated',
					icon: 'fab fa-html5',
					component: 'DateRangeItem',
					value: '',
				},
				{
					name: 'dashboard',
					label: 'Dashboard',
					icon: 'fad fa-window-alt',
					component: 'SelectItem',
					value: '',
					options: [
						{ id: 'town-web', name: 'Town Web' },
						{ id: 'unified', name: 'Unified' },
					],
				},
				{
					name: 'project_manager_id',
					label: 'Project Manager',
					icon: 'fad fa-user-tie',
					component: 'SelectItem',
					value: '',
					// Filled in created() from the staff directory. ModernFilter hides
					// a SelectItem while its options are empty, so this has to be
					// populated before the user can open the filter dropdown.
					options: [],
				},
			],
		}
	},
	computed: {
		...mapState(['authUser', 'tokenJuniper']),
		...mapGetters(['isAuth', 'projectManagerById']),
		toggleableColumns() {
			return TABLE_COLUMNS.filter(col => !col.always)
		},
		tableGridStyle() {
			const widths = TABLE_COLUMNS
				.filter(col => col.always || this.visibleColumns[col.key])
				.map(col => col.width)
			return { gridTemplateColumns: widths.join(' ') }
		},
	},
	created() {
		this.getClientStats()
		this.getClients()
		this.loadProjectManagers()
	},
	mounted() {
		document.addEventListener('click', this.onDocumentClick)
	},
	// Bootstrap tooltips are plain-DOM; re-init after every render so cells that
	// swap content (spinner → ADA badges) always carry a live Tooltip instance.
	// Same dispose-then-create pattern as ClientUsers.vue.
	updated() {
		this.initTooltips()
	},
	beforeDestroy() {
		document.removeEventListener('click', this.onDocumentClick)
		this.disposeTooltips()
		// Generation keeps running server-side; just stop the local counters.
		Object.keys(this.staticGenerating).forEach(id => this.stopStaticTimer(id))
	},
	methods: {
		toggleColumn(key) {
			this.$set(this.visibleColumns, key, !this.visibleColumns[key])
			localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(this.visibleColumns))
		},

		// Close the column-settings dropdown on any click outside of it.
		onDocumentClick(event) {
			if (!this.columnSettingsOpen) return
			const wrap = this.$refs.columnSettings
			if (wrap && !wrap.contains(event.target)) {
				this.columnSettingsOpen = false
			}
		},

		// The PM whitelist feeds the filter dropdown. "Unassigned" comes first —
		// that's the working list for clients nobody has been credited with yet.
		async loadProjectManagers() {
			const managers = await this.$store.dispatch('fetchProjectManagers')
			const filterItem = this.filterItems.find(item => item.name === 'project_manager_id')
			if (!filterItem) return

			filterItem.options = [
				{ id: 'unassigned', name: 'Unassigned' },
				...managers.map(user => ({ id: String(user.id), name: user.name })),
			]
		},

		// Live mc2 name wins so a rename shows up right away; the snapshot stored
		// on the client covers anyone credited who isn't on the current whitelist.
		projectManagerName(client) {
			if (!client.project_manager_id) return ''
			return this.projectManagerById[client.project_manager_id]?.name || client.project_manager_name || ''
		},

		initTooltips() {
			this.$el.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(element => {
				const existing = Tooltip.getInstance(element)
				if (existing) existing.dispose()
				new Tooltip(element)
			})
		},

		disposeTooltips() {
			this.$el.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(element => {
				const existing = Tooltip.getInstance(element)
				if (existing) existing.dispose()
			})
		},

		// Get Clients
		getClients() {
			this.loading = true
			const token = ++this.clientsRequestToken
			juniperApi
				.get('/clients', { params: this.request })
				.then(({ data }) => {
					// Drop response if a newer request has been issued in the meantime.
					if (token !== this.clientsRequestToken) return
					this.clients = data.data
					this.pagination = data
					delete this.pagination.data
					this.clients.forEach(client => {
						client.theme = client.theme || ''
						client.heygov_app_url = client.heygov_app_url || ''
						if (client.options && client.options.length > 0) {
							client.options.forEach(option => {
								if (option.meta_key == 'theme_active') {
									client.theme = option.meta_value
								}
								// HeyGov instance the site is wired to — the dashboard links follow it
								if (option.meta_key == 'heygov_app_url') {
									client.heygov_app_url = option.meta_value
								}
							})
						}
					})
					this.loading = false
					this.loadAdaReports()
				})
				.catch(error => {
					if (token !== this.clientsRequestToken) return
					this.$swal.fire({
						icon: 'warning',
						title: error.message,
						timer: 6000,
					})
					this.loading = false
				})
		},

		// ADA summaries load one request per row (mc2-api), independently of the
		// clients call — each cell shows its own spinner and fills in as its
		// response lands, so a slow audit lookup never blocks the table.
		loadAdaReports() {
			this.adaData = {}
			this.clients.forEach(client => {
				this.$set(this.adaData, client.id, { loading: true, report: null })
				api
					.get(`/juniper/ada-auditor/clients/${client.id}/audit-reports`, { params: { limit: 5 } })
					.then(({ data }) => {
						const items = (data && data.items) || []
						// Latest run may still be in progress — show the newest finished one.
						const report = items.find(r => r.status === 'finished') || null
						this.$set(this.adaData, client.id, { loading: false, report })
					})
					.catch(() => {
						this.$set(this.adaData, client.id, { loading: false, report: null })
					})
			})
		},

		adaState(clientId) {
			return this.adaData[clientId] || { loading: false, report: null }
		},

		openAdaModal(client) {
			const state = this.adaState(client.id)
			if (!state.report) return
			this.adaModal = { clientId: client.id, reportId: state.report.id }
		},

		isGeneratingStatic(clientId) {
			return !!this.staticGenerating[clientId]
		},

		generateStatic(client) {
			if (this.staticGenerating[client.id]) return

			const state = { seconds: 0, timer: null }
			this.$set(this.staticGenerating, client.id, state)
			state.timer = setInterval(() => {
				state.seconds++
			}, 1000)

			juniperApi
				.post('/clients/' + client.id + '/static/generate')
				.then(() => {
					const elapsed = state.seconds
					this.stopStaticTimer(client.id)
					// getClients() may have replaced the row objects meanwhile —
					// update the row currently in the table, not the captured one.
					const row = this.clients.find(c => c.id === client.id)
					if (row) row.last_static_generated_at = new Date().toISOString()
					this.showStaticToast(client.name, elapsed)
				})
				.catch(error => {
					this.stopStaticTimer(client.id)
					this.$swal.fire({
						icon: 'warning',
						title: 'Static generation failed',
						text: (error.response && error.response.data && error.response.data.message) || error.message,
					})
				})
		},

		stopStaticTimer(clientId) {
			const state = this.staticGenerating[clientId]
			if (state && state.timer) clearInterval(state.timer)
			this.$delete(this.staticGenerating, clientId)
		},

		// Bootstrap toast (bottom-right) confirming a finished full-static run.
		// Rendered declaratively via the "show" class so Vue owns the DOM —
		// no imperative bootstrap Toast instance to fight v-for re-renders.
		showStaticToast(clientName, elapsed) {
			const id = ++this.staticToastSeq
			this.staticToasts.push({ id, name: clientName, elapsed })
			setTimeout(() => this.dismissStaticToast(id), 8000)
		},

		dismissStaticToast(id) {
			this.staticToasts = this.staticToasts.filter(toast => toast.id !== id)
		},

		adaOkTooltip(report) {
			const total = report.total_pages || 0
			return `<strong>${total}/${total}</strong> pages error-free<br>${this.adaAuditedLine(report)}`
		},

		adaErrorTooltip(report) {
			const total = report.total_pages || 0
			return (
				`<strong>${report.poor_pages}</strong> of <strong>${total}</strong> pages have errors` +
				(report.errors ? ` (${report.errors} errors total)` : '') +
				`<br>${this.adaAuditedLine(report)}`
			)
		},

		adaWarnTooltip(report) {
			const total = report.total_pages || 0
			return (
				`<strong>${report.warn_pages}</strong> of <strong>${total}</strong> pages have warnings` +
				(report.warnings ? ` (${report.warnings} warnings total)` : '') +
				`<br>${this.adaAuditedLine(report)}`
			)
		},

		adaAuditedLine(report) {
			const at = report.finished_at || report.created_at
			if (!at) return 'Audit date unknown'
			return `Audited ${fromNowUtc(at)} (${formatUtc(at, 'MMM D, YYYY [at] h:mm A')})`
		},

		getClientStats() {
			// Send current filters (excluding staging/environment filter)
			const params = {
				search: this.request.search,
				status: this.request.status,
				theme: this.request.theme,
				dashboard: this.request.dashboard,
				project_manager_id: this.request.project_manager_id,
				launched_from: this.request.launched_from,
				launched_to: this.request.launched_to,
				static_from: this.request.static_from,
				static_to: this.request.static_to,
			}

			const token = ++this.statsRequestToken
			juniperApi
				.get('/clients/stats', { params })
				.then(({ data }) => {
					if (token !== this.statsRequestToken) return
					this.stats = data
				})
				.catch(error => {
					if (token !== this.statsRequestToken) return
					console.error('Error loading stats:', error)
				})
		},

		// Paginate - when click on paginate buttons
		paginate(url) {
			if (!url) return
			let page = 1
			const urlParts = url.split('?')
			if (urlParts.length > 1) {
				page = urlParts[1].replace('page=', '')
			}
			this.request.page = page
			this.getClients()
		},

		// Filter methods
		setEnvironmentFilter(filter) {
			this.environmentFilter = filter
			if (filter === 'all') {
				this.request.staging = ''
			} else if (filter === 'live') {
				this.request.staging = 'live'
			} else if (filter === 'staging') {
				this.request.staging = 'staging'
			}
			this.request.page = 1
			this.getClients()
			this.getClientStats()
		},

		updateFilter({ name, value }) {
			// Update the filter item value
			const filterItem = this.filterItems.find(item => item.name === name)
			if (filterItem) {
				filterItem.value = value
			}

			// Handle date range filters
			if (name === 'launched_at') {
				if (value) {
					const [start, end] = value.split(',')
					this.request.launched_from = start || ''
					this.request.launched_to = end || ''
				} else {
					this.request.launched_from = ''
					this.request.launched_to = ''
				}
			} else if (name === 'static_generated_at') {
				if (value) {
					const [start, end] = value.split(',')
					this.request.static_from = start || ''
					this.request.static_to = end || ''
				} else {
					this.request.static_from = ''
					this.request.static_to = ''
				}
			} else {
				// For other filters, update the request directly
				this.request[name] = value
			}

			this.request.page = 1
			this.getClients()
			this.getClientStats()
		},

		// Search fires on Enter only — keeps the server idle while the user types
		// and avoids race conditions from out-of-order keystroke-triggered requests.
		onSearchSubmit() {
			this.request.page = 1
			this.getClients()
			this.getClientStats()
		},

		// X button next to the search input: wipe the term and immediately refresh
		// the list. Used so the user doesn't have to manually press Enter after
		// clearing.
		clearSearch() {
			this.request.search = ''
			this.onSearchSubmit()
		},

		hasActiveFilters() {
			return (
				this.environmentFilter !== 'all' ||
				this.request.search !== '' ||
				this.request.status !== '' ||
				this.request.theme !== '' ||
				this.request.dashboard !== '' ||
				this.request.project_manager_id !== '' ||
				this.request.launched_from !== '' ||
				this.request.launched_to !== '' ||
				this.request.static_from !== '' ||
				this.request.static_to !== ''
			)
		},

		isUnifiedDashboard(client) {
			return client?.dashboard === 'unified'
		},

		// Formatting methods
		formatType(type) {
			if (!type) return 'Municipal'
			const types = {
				municipal: 'Municipal',
				business: 'Business',
				internal: 'Internal',
			}
			return types[type] || type.charAt(0).toUpperCase() + type.slice(1)
		},

		getEffectiveStatus(client) {
			const rawStatus = (client?.status || '').toLowerCase()
			// Live is valid only when explicit status is live and staging is off.
			if (rawStatus === 'live') {
				return client?.staging ? 'preparing' : 'live'
			}
			return rawStatus || 'live'
		},

		formatStatus(client) {
			const status = this.getEffectiveStatus(client)
			const statuses = {
				live: 'Live',
				preparing: 'Preparing',
				development: 'Development',
				abandoned: 'Abandoned',
				left: 'Left',
				deleted: 'Deleted',
			}
			return statuses[status] || status.charAt(0).toUpperCase() + status.slice(1)
		},

		getStatusClass(client) {
			return this.getEffectiveStatus(client)
		},

		getStatusIcon(client) {
			const status = this.getEffectiveStatus(client)
			const icons = {
				live: 'fad fa-check-circle',
				preparing: 'fad fa-wrench',
				development: 'fad fa-code',
				abandoned: 'fad fa-times-circle',
				left: 'fad fa-arrow-right',
				deleted: 'fad fa-trash',
			}
			return icons[status] || 'fad fa-circle'
		},

		shouldUseStagingUrl(client) {
			const status = this.getEffectiveStatus(client)
			if (status === 'live') return false
			if (status === 'preparing') return true
			return !!client.staging
		},

		// Staging domain shown as a smaller second line under the real domain
		shouldShowStagingUrlBelow(client) {
			if (!client.staging_url) return false
			if (this.shouldUseStagingUrl(client)) return false
			return client.staging_url !== client.url
		},

		formatTheme(theme) {
			if (!theme || theme === 'default') return 'Maverick'
			return theme.charAt(0).toUpperCase() + theme.slice(1)
		},

		formatStaticAt(staticAt) {
			return fromNowUtc(staticAt)
		},

		formatStaticAtFull(staticAt) {
			return formatUtc(staticAt)
		},

		formatLaunchedAt(launchedAt) {
			if (!launchedAt) return 'Not launched'
			return fromNowUtc(launchedAt)
		},

		formatLaunchedAtFull(launchedAt) {
			if (!launchedAt) return 'Not launched yet'
			return formatUtc(launchedAt)
		},

		formatPaginationLabel(label) {
			if (!label) return ''
			return label.replace('&laquo;', '«').replace('&raquo;', '»')
		},

		getDashboardLoginUrl(client) {
			return buildDashboardLoginUrl(client, {
				dashboardUrl: this.dashboardUrl,
				email: this.authUser.email,
				tokenJuniper: this.tokenJuniper,
			})
		},
		// The copy's own dashboard: which one depends on the COPY's dashboard
		// setting, not the live client's (a town-web site can be upgraded onto
		// the unified dashboard).
		getUpgradeDashboardLoginUrl(client) {
			// The copy's options are not in the list payload; it cloned the
			// live client's heygov_app_url, so the live value is the copy's too.
			return buildDashboardLoginUrl(client.upgrade, {
				dashboardUrl: this.dashboardUrl,
				email: this.authUser.email,
				tokenJuniper: this.tokenJuniper,
				target: 'upgrade',
				appUrl: client.heygov_app_url,
			})
		},
	},
}
</script>
<style scoped>
/* Clients Section */
.clients-section {
	background: white;
	border-radius: 8px;
	overflow: hidden;
}


/* Filter Tabs Container */
.filter-tabs-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 24px 24px 24px 24px;
}

.filter-tabs {
	display: flex;
	gap: 0;
	background-color: #f3f4f6;
	border-radius: 25px;
	padding: 4px;
}

.filter-tab {
	background: none;
	border: none;
	padding: 8px 16px;
	font-size: 14px;
	font-weight: 500;
	color: #6b7280;
	cursor: pointer;
	border-radius: 20px;
	transition: all 0.2s ease;
	white-space: nowrap;
	display: flex;
	align-items: center;
	gap: 8px;
}

.filter-tab.active {
	background-color: #ffffff;
	color: #111827;
	box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.filter-tab:hover:not(.active) {
	color: #374151;
}

.filter-tab-text {
	font-weight: 500;
}

.filter-tab-badge {
	background-color: #e5e7eb;
	color: #6b7280;
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 600;
	min-width: 20px;
	text-align: center;
}

.filter-tab.active .filter-tab-badge {
	background-color: #dbeafe;
	color: #1e40af;
}

.environment-tab:not(:last-child) {
	border-right: 1px solid #e9ecef;
}

.tabs-divider {
	width: 1px;
	height: 24px;
	background-color: #dee2e6;
	margin: 0 8px;
	align-self: center;
}

/* Modern Filter Button */
.modern-filter-wrapper .filter-tab {
	background-color: #e5e7eb;
	color: #6b7280;
	border: none;
	font-weight: 600;
}

.modern-filter-wrapper .filter-tab:hover {
	background-color: #d1d5db;
	color: #374151;
}

/* Search Input */
.search-input-wrapper {
	position: relative;
	width: 300px;
}

.search-icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: #9ca3af;
	font-size: 14px;
}

.search-input {
	width: 100%;
	padding: 8px 32px 8px 36px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 14px;
	outline: none;
	transition: border-color 0.2s ease;
	background: white;
}

.search-input:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-clear-btn {
	position: absolute;
	right: 8px;
	top: 50%;
	transform: translateY(-50%);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	padding: 0;
	border: 0;
	border-radius: 50%;
	background: #e5e7eb;
	color: #6b7280;
	font-size: 11px;
	cursor: pointer;
	transition: background 0.15s ease, color 0.15s ease;
}

.search-clear-btn:hover {
	background: #d1d5db;
	color: #1f2937;
}

.search-clear-btn:focus-visible {
	outline: 2px solid #3b82f6;
	outline-offset: 1px;
}

/* Clients Table */
.clients-table {
	border-top: 1px solid #f3f4f6;
}

.table-header {
	display: grid;
	grid-template-columns: 2fr 100px 105px 2fr 105px 110px 105px 100px 120px 150px;
	gap: 12px;
	align-items: center;
	padding: 12px 24px;
	background-color: #f9fafb;
	border-bottom: 1px solid #f3f4f6;
}

.header-cell {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.header-cell.actions-col {
	display: flex;
	justify-content: flex-end;
	align-items: center;
}

/* Column settings (header gear dropdown) */
.column-settings {
	position: relative;
}

.column-settings-btn {
	background: none;
	border: none;
	padding: 2px 6px;
	border-radius: 4px;
	color: #9ca3af;
	font-size: 14px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.column-settings-btn:hover,
.column-settings-btn.is-open {
	color: #374151;
	background-color: #eef1f5;
}

.column-settings-dropdown {
	position: absolute;
	top: calc(100% + 6px);
	right: 0;
	z-index: 20;
	min-width: 170px;
	padding: 8px 0;
	background: #fff;
	border: 1px solid #e6e8ec;
	border-radius: 10px;
	box-shadow: 0 8px 24px rgba(17, 24, 39, 0.12);
}

.column-settings-title {
	padding: 4px 14px 8px;
	font-size: 11px;
	font-weight: 600;
	color: #9ca3af;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.column-settings-option {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 6px 14px;
	margin: 0;
	font-size: 13px;
	font-weight: 400;
	color: #111827;
	text-transform: none;
	letter-spacing: normal;
	cursor: pointer;
	user-select: none;
}

.column-settings-option:hover {
	background-color: #f3f5f9;
}

.column-settings-option input[type='checkbox'] {
	margin: 0;
	accent-color: #111827;
	cursor: pointer;
}

.table-row {
	display: grid;
	grid-template-columns: 2fr 100px 105px 2fr 105px 110px 105px 100px 120px 150px;
	gap: 12px;
	align-items: center;
	padding: 16px 24px;
	border-bottom: 1px solid #f9fafb;
	transition: background-color 0.2s ease;
}

.table-row:hover {
	background-color: #f9fafb;
}

.table-row:last-child {
	border-bottom: none;
}

.table-cell {
	font-size: 14px;
	color: #111827;
}

/* Client Info */
.client-info {
	display: flex;
	align-items: center;
	gap: 12px;
}

.client-avatar {
	width: 48px;
	height: 48px;
	border-radius: 8px;
	overflow: hidden;
	flex-shrink: 0;
	background-color: #f3f4f6;
	display: flex;
	align-items: center;
	justify-content: center;
}

.client-avatar img {
	width: 100%;
	height: 100%;
	object-fit: contain;
}

.client-avatar-placeholder {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #9ca3af;
	font-size: 20px;
}

.client-details {
	min-width: 0;
}

.client-name {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
	line-height: 1.4;
	display: flex;
	align-items: center;
	gap: 8px;
}

.client-name a {
	color: #111827;
	text-decoration: none;
	transition: color 0.2s ease;
}

.client-name a:hover {
	color: #3b82f6;
}

.dashboard-badge {
	background-color: #ecfdf5;
	color: #047857;
	border: 1px solid #a7f3d0;
	padding: 2px 8px;
	border-radius: 9999px;
	font-size: 11px;
	font-weight: 600;
	line-height: 1;
	text-transform: uppercase;
	letter-spacing: 0.03em;
}

/* Type Badge */
.type-badge {
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	display: inline-block;
}

.type-badge.municipal {
	background-color: #dbeafe;
	color: #1e40af;
}

.type-badge.business {
	background-color: #fef3c7;
	color: #92400e;
}

.type-badge.internal {
	background-color: #fee2e2;
	color: #991b1b;
}

/* Status Badge */
.status-badge {
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	text-transform: capitalize;
	display: inline-flex;
	align-items: center;
	gap: 4px;
}

.status-badge i {
	font-size: 10px;
}

.status-badge.live {
	background-color: #dcfce7;
	color: #166534;
}

.status-badge.preparing {
	background-color: #fef3c7;
	color: #92400e;
}

.status-badge.development {
	background-color: #dbeafe;
	color: #1e40af;
}

.status-badge.abandoned {
	background-color: #fee2e2;
	color: #991b1b;
}

.status-badge.left {
	background-color: #e0e7ff;
	color: #4338ca;
}

.status-badge.deleted {
	background-color: #f3f4f6;
	color: #6b7280;
}

/* URL Cell */
.url-stack {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 2px;
	min-width: 0;
}

.url-link--secondary {
	font-size: 11px;
	opacity: 0.75;
}

.url-link--secondary:hover {
	opacity: 1;
}

.url-link {
	color: #6b7280;
	text-decoration: none;
	font-size: 13px;
	display: inline-flex;
	align-items: center;
	transition: color 0.2s ease;
}

.url-link:hover {
	color: #3b82f6;
	text-decoration: underline;
}

.url-link.live {
	color: #059669;
}

.url-link.staging {
	color: #6366f1;
}

/* Launched Cell */
.launched-date {
	color: #6b7280;
	font-size: 13px;
}

/* Static Cell */
.static-date {
	color: #6b7280;
	font-size: 13px;
	cursor: default;
	display: inline-flex;
	align-items: center;
	gap: 5px;
}

.static-html5-icon {
	color: #9ca3af;
	font-size: 12px;
}

.cell-empty {
	color: #d1d5db;
	font-size: 13px;
	cursor: default;
}

/* ADA Cell */
.ada-spinner {
	color: #9ca3af;
	width: 16px;
	height: 16px;
	border-width: 2px;
}

.ada-badges {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	text-decoration: none;
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
}

.ada-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 24px;
	height: 24px;
	padding: 0 6px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 700;
	line-height: 1;
	transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.ada-badges:hover .ada-badge {
	transform: translateY(-1px);
	box-shadow: 0 2px 6px rgba(15, 23, 42, 0.18);
}

.ada-badge--ok {
	background-color: #dcfce7;
	color: #16a34a;
	font-size: 10px;
}

.ada-badge--error {
	background-color: #dc2626;
	color: #ffffff;
}

.ada-badge--warning {
	background-color: #2563eb;
	color: #ffffff;
}

/* Theme Badge */
.theme-badge {
	color: #6b7280;
	font-size: 13px;
	display: inline-flex;
	align-items: center;
}

/* PM Cell */
.pm-name {
	color: #6b7280;
	font-size: 13px;
	display: block;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	cursor: default;
}

/* Actions */
.actions-cell {
	display: flex;
	flex-direction: column;
	gap: 2px;
	align-items: flex-end;
	justify-content: center;
}

.actions-row {
	display: flex;
	gap: 2px;
	align-items: center;
	justify-content: flex-end;
}

.action-icon-btn {
	background: none;
	border: none;
	padding: 5px 6px;
	border-radius: 4px;
	color: #9ca3af;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	text-decoration: none;
}

.action-icon-btn:hover {
	background-color: #f3f4f6;
	color: #6b7280;
	text-decoration: none;
}
.action-icon-btn--blue { color: #1d4ed8; }
.action-icon-btn--blue:hover { background-color: #dbeafe; color: #1d4ed8; }
.action-icon-btn--sky { color: #38bdf8; }
.action-icon-btn--sky:hover { background-color: #e0f2fe; color: #38bdf8; }
.action-icon-btn--orange { color: #f97316; }
.action-icon-btn--orange:hover { background-color: #ffedd5; color: #f97316; }
.action-icon-btn--teal { color: #0f766e; }
.action-icon-btn--teal:hover { background-color: #ccfbf1; color: #0f766e; }
.action-icon-btn--purple { color: #5b21b6; }
.action-icon-btn--purple:hover { background-color: #ede9fe; color: #4c1d95; }

.theme-palette-icon { color: #a855f7; }

/* Generate Full Static button — spinner + live seconds while the build runs */
.static-gen-btn.is-generating {
	cursor: default;
	background-color: #ffedd5;
	color: #f97316;
}

.static-gen-btn:disabled {
	opacity: 1;
}

.static-gen-spinner {
	width: 12px;
	height: 12px;
	border-width: 2px;
}

.static-gen-seconds {
	font-size: 11px;
	font-weight: 700;
	margin-left: 4px;
	min-width: 18px;
	text-align: right;
	font-variant-numeric: tabular-nums;
}

.static-toast-container {
	z-index: 1090;
}

/* Loading State — blur the table body and overlay a centered label */
.table-body-wrap {
	position: relative;
}
.table-body-wrap.is-loading {
	min-height: 240px;
}
.table-body.is-blurred {
	filter: blur(2.5px);
	opacity: 0.55;
	pointer-events: none;
	user-select: none;
	transition: filter 0.15s ease, opacity 0.15s ease;
}
.table-loading {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 5;
}
.table-loading__box {
	display: inline-flex;
	align-items: center;
	background: #ffffff;
	color: #374151;
	font-size: 14px;
	font-weight: 600;
	padding: 10px 20px;
	border-radius: 999px;
	box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);
	border: 1px solid #eef0f2;
}

/* Empty State */
.empty-state {
	padding: 60px 24px;
	text-align: center;
	border-bottom: 1px solid #f3f4f6;
}

.empty-state-content {
	max-width: 500px;
	margin: 0 auto;
}

.empty-state-icon {
	width: 80px;
	height: 80px;
	background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto 24px;
	color: #9ca3af;
	font-size: 32px;
}

.empty-state-title {
	font-size: 20px;
	font-weight: 600;
	color: #111827;
	margin: 0 0 12px 0;
}

.empty-state-description {
	font-size: 14px;
	color: #6b7280;
	line-height: 1.6;
	margin: 0 0 32px 0;
}

.empty-state-actions {
	margin-bottom: 32px;
}

.empty-state-btn {
	display: inline-flex;
	align-items: center;
	padding: 12px 24px;
	background-color: #3b82f6;
	color: #ffffff;
	text-decoration: none;
	border-radius: 8px;
	font-weight: 500;
	font-size: 14px;
	transition: all 0.2s ease;
}

.empty-state-btn:hover {
	background-color: #2563eb;
	color: #ffffff;
	text-decoration: none;
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

/* Status Info Section */
.status-info-section {
	padding: 24px;
	background-color: #f9fafb;
	border-top: 1px solid #f3f4f6;
}

.status-info-title {
	font-size: 13px;
	font-weight: 600;
	color: #374151;
	margin-bottom: 12px;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.status-info-items {
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
}

.status-info-item {
	display: flex;
	align-items: center;
	gap: 8px;
}

.status-info-text {
	font-size: 13px;
	color: #6b7280;
}

/* Pagination — clean Prev / page / Next */
.cl-pagination {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px 24px;
	border-top: 1px solid #f3f4f6;
}
.cl-pagination__info {
	font-size: 14px;
	color: #6b7280;
}
.cl-pagination__info strong {
	color: #111827;
	font-weight: 600;
}
.cl-pagination__controls {
	display: flex;
	align-items: center;
	gap: 10px;
}
.cl-page-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 8px 14px;
	border: 1px solid #e5e7eb;
	background: #fff;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 600;
	color: #374151;
	cursor: pointer;
	transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.cl-page-btn:hover:not(:disabled) {
	background: #f3f4f6;
	border-color: #d1d5db;
	color: #111827;
}
.cl-page-btn:disabled {
	opacity: 0.45;
	cursor: not-allowed;
}
.cl-page-btn i {
	font-size: 11px;
}
.cl-page-current {
	font-size: 13px;
	font-weight: 600;
	color: #374151;
	min-width: 92px;
	text-align: center;
}
.cl-page-current__sep {
	color: #9ca3af;
	margin: 0 2px;
}

/* Filter Button Styling */
.filter-dropdown {
	display: inline-block;
	margin-right: 8px;
}

.filter-dropdown .btn {
	white-space: nowrap;
	font-size: 14px;
	padding: 6px 12px;
	border-radius: 6px;
}

.filter-wrapper {
	display: flex;
	align-items: center;
	gap: 8px;
}

.filter-wrapper > * {
	margin-right: 8px;
}

.filter-wrapper > *:last-child {
	margin-right: 0;
}
</style>
